/**
 * LoginScreen — the front door. Calm light canvas, ink masthead, one accent
 * action. The activity log below the form prints REAL auth events only.
 *
 * E2E contract: input[autocomplete="username"], input[type="password"],
 * button "Connect", button "Create account", and the testids "login-error",
 * "login-notice" and "login-forgot-password".
 *
 * OMEGA-814 — the field is labelled Email and the login identity IS the email. The
 * autocomplete token stays "username" deliberately: per the HTML spec that is the
 * token for "the identifier you sign in with, whatever its form", and it is what a
 * password manager matches a saved credential on. Switching it to "email" would make
 * managers offer a contact address rather than the stored sign-in credential, and would
 * silently break the saved logins of every customer who already has one. The input
 * keeps type="text" for the same reason a legacy account must still work: type="email"
 * would make the browser reject a pre-OMEGA-814 username as malformed before it could
 * ever be submitted.
 *
 * L01: a 403 email_not_verified (needsVerification) is a recoverable state, not
 * a wrong-credentials failure — it renders in a neutral notice box with an
 * accent (interactive) "Resend verification email" affordance, distinct from the
 * red danger box used for a 401.
 *
 * F14: `onForgotPassword` opens the recovery view (FR2) and `notice` carries a
 * neutral message from a screen the user has already left — a completed reset
 * (FR10) or a completed password change (FR11), both of which end the session
 * and so cannot show their own confirmation. `notice` is deliberately NOT
 * `error`: it renders in the neutral box, never the red one, because neither
 * case is a failure.
 */

export default function LoginScreen({
  user, setUser, password, setPassword, loginLog, onConnect, onRegister, error,
  needsVerification, onResendVerification, authenticating = false,
  resendingVerification = false, resendCooldown = 0,
  onForgotPassword, notice = "",
}) {
  // OMEGA-563 — the resend triggers a real outbound email; lock it while a send
  // is in flight and for the backend cooldown window after one succeeds.
  const resendDisabled = resendingVerification || resendCooldown > 0;
  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Masthead */}
        <div className="bg-ink rounded-none px-7 pt-7 pb-6">
          <div className="text-white font-semibold text-2xl tracking-[0.22em]">OMEGA AI HPC</div>
          <div className="text-ink-muted text-sm mt-1">Console</div>
        </div>

        {/* Form card */}
        <div className="bg-surface border border-line border-t-0 rounded-none px-7 py-6">
          {/* F15 FR3 (OMEGA-800) — htmlFor/id, so the label NAMES the input rather than
              sitting above it. Without this the field has no accessible name, and a naming
              test could only prove the string is on the page. Ids are prefixed per screen:
              both auth screens can be mounted in one test run, and FR4 requires the six to
              be mutually distinct.

              OMEGA-814 keeps that association and changes the label to Email, with the id
              `login-identifier`. F15 landed it as `login-username` a day before the login
              identity moved. The field takes an email for any account created from
              migration 091 onward and the original handle for anything older, which is
              exactly why the wire key is `identifier` rather than `email`; an id reading
              `username` on it would be the conflation that key was named to avoid.

              `autoComplete` stays "username" and is not a leftover: it is the correct value
              for a sign-in identity field whatever that identity looks like, and it is what
              a password manager pairs with `current-password`. "email" belongs on a
              registration/contact field, which is where RegisterScreen uses it. */}
          <label htmlFor="login-identifier" className="block text-xs text-fg2 uppercase tracking-wide mb-1">
            Email
          </label>
          <input
            id="login-identifier"
            value={user}
            onChange={(e) => setUser(e.target.value)}
            autoComplete="username"
            className="w-full px-3 py-2 mb-4 rounded-none border border-line bg-surface text-fg text-sm outline-none focus:border-accent-fill"
          />

          <label htmlFor="login-password" className="block text-xs text-fg2 uppercase tracking-wide mb-1">
            Password
          </label>
          <input
            id="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            onKeyDown={(e) => e.key === "Enter" && !authenticating && onConnect()}
            /* The forgot-password row below owns the gap when it renders, so the
               field drops its own margin only then; without the row (the prop is
               optional) the original spacing is preserved exactly. */
            className={`w-full px-3 py-2 ${onForgotPassword ? "" : "mb-4"} rounded-none border border-line bg-surface text-fg text-sm outline-none focus:border-accent-fill`}
          />

          {/* F14 FR2 — adjacent to the password interaction, and a real <button>
              so it is reachable and activatable by keyboard without any extra
              handling (a styled <div> would not be). */}
          {onForgotPassword && (
            <div className="flex justify-end mb-4 mt-1">
              <button
                type="button"
                onClick={onForgotPassword}
                data-testid="login-forgot-password"
                className="text-xs text-accent cursor-pointer hover:text-accent-strong bg-transparent border-0 p-0"
              >
                Forgot password?
              </button>
            </div>
          )}

          {notice && (
            <div
              data-testid="login-notice"
              role="status"
              className="text-sm text-fg2 bg-surface2 border border-line rounded-none px-3 py-2 mb-4 leading-relaxed"
            >
              {notice}
            </div>
          )}

          {error && (
            needsVerification ? (
              <div className="text-sm text-fg2 bg-surface2 border border-line rounded-none px-3 py-2 mb-4">
                <div data-testid="login-verify-notice">{error}</div>
                <button
                  type="button"
                  onClick={onResendVerification}
                  disabled={resendDisabled}
                  data-testid="login-resend"
                  className="mt-2 text-sm text-accent cursor-pointer hover:text-accent-strong bg-transparent border-0 p-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendingVerification
                    ? "Sending..."
                    : resendCooldown > 0
                      ? `Resend available in ${resendCooldown}s`
                      : "Resend verification email"}
                </button>
              </div>
            ) : (
              <div
                data-testid="login-error"
                className="text-sm text-danger bg-danger-tint border border-danger/20 rounded-none px-3 py-2 mb-4"
              >
                {error}
              </div>
            )
          )}

          <button
            onClick={onConnect}
            disabled={authenticating}
            aria-busy={authenticating}
            className="w-full py-2.5 rounded-none bg-accent-fill text-white text-sm font-medium cursor-pointer hover:bg-accent-strong transition-colors duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {authenticating ? "Connecting..." : "Connect"}
          </button>

          {onRegister && (
            <button
              onClick={onRegister}
              className="w-full py-2 mt-2 text-sm text-accent cursor-pointer hover:text-accent-strong bg-transparent border-0"
            >
              Create account
            </button>
          )}

          {loginLog.length > 0 && (
            <div className="mt-5 pt-4 border-t border-line max-h-36 overflow-y-auto">
              {loginLog.map((l, i) => (
                <div key={i} className="text-xs font-mono text-fg2 leading-relaxed">
                  {l}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
